import { EventEmitter, Injectable } from '@angular/core';
import {
  CreateSurveyResultInput,
  CreateSurveyResultMutation,
  CreateSurveySchemaInput,
  CreateSurveySchemaMutation,
  LoadSurveySchemaQuery,
  LoadSurveySchemaResultsQuery,
  LoadSurveySchemasQuery,
  LoadSurveySchemasQueryVariables,
  SurveySchema,
  UpdateSurveySchemaInput,
  UpdateSurveySchemaMutation
} from '@queries/generated';
import { finalMap } from '@rx/final-map';
import { filter, map, tap } from 'rxjs/operators';

import { slk } from 'survey-core';

slk('MTNlYTYxODYtNDM2ZC00ZWRhLTkwMTEtZmY3YjUxZWZjZTg1OzE9MjAyNS0wMy0yOCwyPTIwMjUtMDMtMjgsND0yMDI1LTAzLTI4');

@Injectable({
  providedIn: 'root'
})
export class SurveysService {
  events$ = new EventEmitter<string>();

  constructor(
    private loadSurveySchemasQuery: LoadSurveySchemasQuery,
    private loadSurveySchemaQuery: LoadSurveySchemaQuery,
    private loadSurveySchemaResultsQuery: LoadSurveySchemaResultsQuery,
    private createSurveySchemaMutation: CreateSurveySchemaMutation,
    private updateSurveySchemaMutation: UpdateSurveySchemaMutation,
    private createSurveyResultMutation: CreateSurveyResultMutation
  ) {}

  load(variables: LoadSurveySchemasQueryVariables) {
    const query = this.loadSurveySchemasQuery.watch(variables);
    return query.valueChanges.pipe(
      map(({ data }) => data.surveySchemas.all),
      tap(({ pageInfo }) => {
        if (pageInfo.hasNextPage) {
          const after = pageInfo.endCursor;
          query.fetchMore({ variables: { ...variables, after } });
        }
      }),
      map(({ nodes }) => nodes)
    );
  }

  loadSurveyResults = (id: string) => {
    return this.loadSurveySchemaResultsQuery
      .watch({ id })
      .valueChanges.pipe(map(({ data }) => data.node as SurveySchema));
  };

  find = (id: string) => {
    return this.loadSurveySchemaQuery.watch({ id }).valueChanges.pipe(map(({ data }) => data.node as SurveySchema));
  };

  create(input: CreateSurveySchemaInput) {
    return this.createSurveySchemaMutation.mutate({ input }, { refetchQueries: ['LoadSurveySchemas'] }).pipe(
      map(({ data }) => data.createSurveySchema),
      finalMap('surveySchema')
    );
  }

  update(input: UpdateSurveySchemaInput) {
    return this.updateSurveySchemaMutation.mutate({ input }).pipe(
      map(({ data }) => data.updateSurveySchema),
      finalMap('surveySchema')
    );
  }

  createResult(input: CreateSurveyResultInput) {
    return this.createSurveyResultMutation.mutate({ input }).pipe(map(({ data }) => data.createSurveyResult.ok));
  }

  trigger(event: string) {
    this.events$.emit(event);
  }

  listen(event: string) {
    return this.events$.pipe(filter((e) => e === event));
  }
}
